/**
 * A plan's recurring commission in one line (TMMRSL-0049): "10% for 12 months", "10% for life", or
 * each Advanced phase joined ("20%, months 1–3 · 10% from month 4"). Pure apart from `t`. Used by
 * the plans list, and by the affiliate deal's plan picker (TMMRSL-0050). A plan with a bounty
 * (TMMAPI-0105, TMMRSL-0051) appends it: "10% for 12 months + a $50.00 bounty".
 */
import type { TFunction } from 'i18next';
import type { CommissionPhase, CommissionPlanMode, PlanExtras } from '@/api/commissionPlans';
import { moneyIn } from '@/components/moneyIn';
import { bpToPercentText } from '@/i18n/format';

/** A plan, or a write body: a missing mode reads as Advanced, so every phase shows. */
export interface PlanSummarySource {
  mode?: CommissionPlanMode;
  phases?: ReadonlyArray<CommissionPhase>;
  /** The plan's currency — the bounty's. */
  currency?: string;
  extras?: PlanExtras | null;
}

function phaseText(phase: CommissionPhase, locale: string, t: TFunction): string {
  const rate = bpToPercentText(phase.rateBp, locale);
  if (phase.toMonth === null) {
    return t('commissionPlans.summary.phaseOnward', { rate, from: phase.fromMonth });
  }
  if (phase.toMonth === phase.fromMonth) {
    return t('commissionPlans.summary.phaseMonth', { rate, from: phase.fromMonth });
  }
  return t('commissionPlans.summary.phaseRange', {
    rate,
    from: phase.fromMonth,
    to: phase.toMonth,
  });
}

function recurringText(plan: PlanSummarySource, locale: string, t: TFunction): string {
  const phases = plan.phases ?? [];
  const first = phases[0];
  if (!first) return '';
  if (plan.mode === 'SIMPLE') {
    const rate = bpToPercentText(first.rateBp, locale);
    return first.toMonth === null
      ? t('commissionPlans.summary.simpleLife', { rate })
      : t('commissionPlans.summary.simpleMonths', {
          rate,
          count: first.toMonth - first.fromMonth + 1,
        });
  }
  return phases
    .map((phase) => phaseText(phase, locale, t))
    .reduce((a, b) => t('commissionPlans.summary.join', { a, b }));
}

export function planSummary(plan: PlanSummarySource, locale: string, t: TFunction): string {
  const a = recurringText(plan, locale, t);
  const bountyMinor = plan.extras?.bountyMinor ?? null;
  if (a === '' || bountyMinor === null) return a;
  const amount = moneyIn(bountyMinor, plan.currency ?? '', locale, t('statements.unavailable'));
  return t('commissionPlans.summary.withBounty', { a, amount });
}
